/**
 * Sol purchase-options selector.
 *
 * Renders the "How often" choice (subscription vs one-time) and feeds the
 * chosen selling plan into Horizon's product form via the `form` attribute,
 * so no fork of buy-buttons.liquid is needed.
 *
 * Two jobs:
 *   1. Re-render itself when the flavour changes. Horizon's variant picker
 *      only morphs itself and resolves a promise carrying the fetched HTML;
 *      every other component updates from that. Same pattern as product-price.js.
 *   2. Publish the selected price as --sol-atc-price so the add-to-cart button
 *      can render "Add to cart — $17" from CSS. Doing it in CSS rather than by
 *      writing into the button keeps morph from clobbering it.
 */
import { StandardEvents } from '@shopify/events';

class SolPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @type {Element | null} */
  #section = null;

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'selling_plan') return;
    this.#sync();
  };

  /** The currently checked plan id, '' for one-time. */
  get selectedPlan() {
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : null;
  }

  #sync() {
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    for (const label of this.querySelectorAll('.sol-buy__option')) {
      label.classList.toggle('sol-buy__option--selected', label.contains(checked));
    }

    const label = checked.dataset.priceLabel;
    const target = this.#section ?? this;
    if (label) target.style.setProperty('--sol-atc-price', `" — ${label}"`);

    this.#syncConsent(checked);
  }

  /**
   * Shopify renders a recurring-payment consent line inside the product form
   * and shows it when a selling plan is chosen. It watches the form, and these
   * radios live outside it, so switching to one-time left the consent showing.
   * Toggle it here, and tell the form its fields changed.
   * @param {HTMLInputElement} checked
   */
  #syncConsent(checked) {
    const form = checked.form;
    if (!form) return;

    const subscribing = checked.value !== '';
    const consent = form.querySelector('#shopify-buyer-consent');
    if (consent) {
      consent.classList.toggle('hidden', !subscribing);
      consent.setAttribute('aria-hidden', String(!subscribing));
    }

    form.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId && !detail.newProduct) return;

        const replacement = detail.html.querySelector(
          `sol-purchase-options[data-block-id="${this.dataset.blockId}"]`
        );
        if (!replacement) return;

        // Keep the shopper's frequency choice across a flavour change, but only
        // when that plan is still offered on the new variant.
        const previous = this.selectedPlan;
        this.innerHTML = replacement.innerHTML;

        if (previous !== null) {
          const match = this.querySelector(`input[name="selling_plan"][value="${CSS.escape(previous)}"]`);
          if (match instanceof HTMLInputElement) match.checked = true;
        }

        this.#sync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sol-purchase-options] promise rejected:', error);
      });
  };
}

if (!customElements.get('sol-purchase-options')) {
  customElements.define('sol-purchase-options', SolPurchaseOptions);
}
